/**
 * dataGate.js — global data-availability gate (module-level singleton).
 *
 * Tracks two independent "stop requesting data" conditions:
 *   • SERVER cooldown — Binance returned 418/429 to our server, so the backend
 *     limiter is cooling down. Polled from GET /api/market/status. Affects ALL
 *     users; data modules should pause until it clears.
 *   • USER throttle — OUR API returned 429 to THIS browser (too many requests
 *     from one client). Set locally; affects only this tab.
 *
 * Components subscribe() to render the banner; data modules call isServerPaused()
 * / isPaused() to skip requests while a cooldown is active.
 */
import { AUTH_API_URL } from '../config';

const state = {
  serverCooldown: false,
  serverRemainingMs: 0,
  userThrottledUntil: 0, // epoch ms
};
const subs = new Set();

export function getState() {
  const now = Date.now();
  return {
    serverCooldown: state.serverCooldown,
    serverRemainingMs: state.serverRemainingMs,
    userThrottled: now < state.userThrottledUntil,
    userRemainingMs: Math.max(0, state.userThrottledUntil - now),
    accumulation: state.accumulation || [],
    accumulationDetail: state.accumulationDetail || {},
  };
}

const emit = () => subs.forEach((fn) => { try { fn(getState()); } catch { /* ignore */ } });

export function subscribe(fn) {
  subs.add(fn);
  fn(getState());
  return () => subs.delete(fn);
}

/** Server-wide Binance cooldown — all data modules should pause. */
export function isServerPaused() { return state.serverCooldown; }

/** Any active pause (server cooldown OR this client's own 429 throttle). */
export function isPaused() { return state.serverCooldown || Date.now() < state.userThrottledUntil; }

/** Call on any HTTP 429 from our API — backs this browser off for `ms`. */
export function reportUserRateLimited(ms = 30_000) {
  const until = Date.now() + Math.max(5_000, ms);
  if (until > state.userThrottledUntil) { state.userThrottledUntil = until; emit(); }
}

let polling = false;
async function poll() {
  try {
    const r = await fetch(`${AUTH_API_URL}/market/status`, { cache: 'no-store' });
    if (!r.ok) return;
    const d = await r.json();
    const cd = !!(d && d.binance && d.binance.coolingDown);
    const rem = (d && d.binance && d.binance.remainingMs) || 0;
    const acc = (d && d.accumulation) || [];
    const detail = (d && d.accumulationDetail) || {};
    // В строку сравнения включаем и state зон, чтобы смена inside→breakout
    // доезжала до подписчиков даже при неизменном списке символов.
    const accStr = acc.join(',') + '|' + Object.entries(detail).map(([s, i]) => s + ':' + i.state).join(',');
    if (cd !== state.serverCooldown || rem !== state.serverRemainingMs || state.accStr !== accStr) {
      state.serverCooldown = cd;
      state.serverRemainingMs = rem;
      state.accStr = accStr;
      state.accumulation = acc;
      state.accumulationDetail = detail;
      emit();
    }
  } catch { /* offline / transient — keep last state */ }
}

/** Start polling the server status (idempotent). */
export function startDataGate() {
  if (polling) return;
  polling = true;
  poll();
  setInterval(poll, 15_000);
}
